/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled, @typescript-eslint/consistent-type-imports
import { css, jsx } from '@emotion/react';
import type { IntlShape } from 'react-intl';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { ReplaceStep } from '@atlaskit/editor-prosemirror/transform';
import { layoutBreakpointWidth } from '@atlaskit/editor-shared-styles/constants';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';

import type { BlockControlsPlugin } from '../blockControlsPluginType';
import { getNodeAnchor } from '../pm-plugins/decorations-common';
import { useActiveAnchorTracker } from '../pm-plugins/utils/active-anchor-tracker';
import { isAnchorSupported } from '../pm-plugins/utils/anchor-utils';
import type { AnchorRectCache } from '../pm-plugins/utils/anchor-utils';
import { getInsertLayoutStep, updateSelection } from '../pm-plugins/utils/update-selection';
import { getAnchorAttrName } from './utils/dom-attr-name';

// 8px gap + 16px on left and right
const DROP_TARGET_LAYOUT_DROP_ZONE_WIDTH = 40;

export type DropTargetLayoutProps = {
	api: ExtractInjectionAPI<BlockControlsPlugin> | undefined;
	formatMessage?: IntlShape['formatMessage'];
	getPos: () => number | undefined;
	parent: PMNode;
};

const dropTargetLayoutStyle = css({
	height: '100%',
	width: `${DROP_TARGET_LAYOUT_DROP_ZONE_WIDTH}px`,
	transform: 'translateX(-50%)',
	zIndex: 120,
	position: 'relative',
	display: 'flex',
	justifyContent: 'center',
});

const dropTargetLayoutHintStyle = css({
	height: '100%',
	position: 'relative',
	borderRight: `${token('border.width')} dashed ${token('color.border.focused')}`,
	width: 0,
});

export const DropTargetLayout = (
	props: DropTargetLayoutProps & {
		anchorRectCache?: AnchorRectCache;
	},
): jsx.JSX.Element | null => {
	const { api, getPos, parent, anchorRectCache } = props;

	const ref = useRef<HTMLDivElement | null>(null);
	const [isDraggedOver, setIsDraggedOver] = useState(false);

	const anchorName = getNodeAnchor(parent);

	const nextNodeAnchorName =
		ref.current?.parentElement?.nextElementSibling?.getAttribute(getAnchorAttrName());
	let height = '100%';
	if (nextNodeAnchorName) {
		if (isAnchorSupported()) {
			height = `anchor-size(${nextNodeAnchorName} height)`;
		} else if (anchorRectCache) {
			const layoutColumnRect = anchorRectCache.getRect(nextNodeAnchorName);
			height = `${layoutColumnRect?.height || 0}px`;
		}
	}

	const dropTargetStackLayoutHintStyle = css({
		// jest warning: JSDOM version (22) doesn't support the new @container CSS rule
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
		[`@container layout-area (max-width:${layoutBreakpointWidth.MEDIUM - 1}px)`]: {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
			height,
			marginTop: `${token('space.050')}`,
		},
	});
	const [isActiveAnchor] = useActiveAnchorTracker(anchorName);

	const { activeNode } = api?.blockControls?.sharedState.currentState() || {};
	const onDrop = useCallback(() => {
		if (!activeNode) {
			return;
		}

		const to = getPos();
		let mappedTo: number | undefined;
		if (to !== undefined) {
			const { pos: from } = activeNode;
			api?.core?.actions.execute(({ tr }) => {
				api?.blockControls?.commands?.moveToLayout(from, to)({ tr });
				const insertColumnStep = getInsertLayoutStep(tr);
				mappedTo = (insertColumnStep as ReplaceStep)?.from;

				return tr;
			});

			api?.core?.actions.execute(({ tr }) => {
				if (mappedTo !== undefined) {
					updateSelection(tr, mappedTo);
				}
				return tr;
			});
		}
	}, [api, getPos, activeNode]);

	useEffect(() => {
		if (ref.current) {
			return dropTargetForElements({
				element: ref.current,
				onDragEnter: () => {
					setIsDraggedOver(true);
				},
				onDragLeave: () => {
					setIsDraggedOver(false);
				},
				onDrop,
			});
		}
	}, [onDrop]);

	if (activeNode?.nodeType === 'layoutSection') {
		return null;
	}
	return (
		<div
			ref={ref}
			// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
			css={[dropTargetLayoutStyle, dropTargetStackLayoutHintStyle]}
			data-testid="block-ctrl-drop-indicator"
		>
			{isDraggedOver ? (
				<DropIndicator edge="right" gap={`-${DROP_TARGET_LAYOUT_DROP_ZONE_WIDTH}px`} />
			) : (
				(isActiveAnchor ||
					expValEquals('platform_editor_native_anchor_with_dnd', 'isEnabled', true) ||
					isExperimentEnabled('platform_editor_block_control_migration')) && (
					<div data-testid="block-ctrl-drop-hint" css={dropTargetLayoutHintStyle}></div>
				)
			)}
		</div>
	);
};

export const DropTargetLayoutNativeAnchorSupport = (
	props: DropTargetLayoutProps & {
		anchorRectCache?: AnchorRectCache;
	},
): jsx.JSX.Element | null => {
	const { api, getPos, parent, anchorRectCache } = props;

	const ref = useRef<HTMLDivElement | null>(null);
	const [isDraggedOver, setIsDraggedOver] = useState(false);
	const anchorName = getNodeAnchor(parent);
	const [nextNodeAnchorName, setNextNodeAnchorName] = useState<string | null>(null);
	const readNextNodeAnchor = useCallback(() => {
		const nextElementSibling = ref.current?.parentElement?.nextElementSibling as HTMLElement | null;
		const attrName = getAnchorAttrName();
		const nextAnchorName = nextElementSibling?.getAttribute(attrName) ?? null;
		setNextNodeAnchorName((prev) => (prev === nextAnchorName ? prev : nextAnchorName));
	}, []);

	const height = useMemo(() => {
		if (nextNodeAnchorName) {
			if (isAnchorSupported()) {
				return `anchor-size(${nextNodeAnchorName} height)`;
			} else if (anchorRectCache) {
				const layoutColumnRect = anchorRectCache.getRect(nextNodeAnchorName);
				return `${layoutColumnRect?.height || 0}px`;
			}
		}
		// Stacked mode fallback: minimal height to avoid oversized hint on first render
		return '0px';
	}, [nextNodeAnchorName, anchorRectCache]);

	useLayoutEffect(() => {
		const raf = requestAnimationFrame(() => {
			readNextNodeAnchor();
		});
		return () => cancelAnimationFrame(raf);
	}, [readNextNodeAnchor]);

	const dropTargetStackLayoutHintStyle = css({
		// jest warning: JSDOM version (22) doesn't support the new @container CSS rule
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
		[`@container layout-area (max-width:${layoutBreakpointWidth.MEDIUM - 1}px)`]: {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
			height,
			marginTop: `${token('space.050')}`,
		},
	});
	const [isActiveAnchor] = useActiveAnchorTracker(anchorName);

	const { activeNode } = api?.blockControls?.sharedState.currentState() || {};
	const onDrop = useCallback(() => {
		if (!activeNode) {
			return;
		}

		const to = getPos();
		let mappedTo: number | undefined;
		if (to !== undefined) {
			const { pos: from } = activeNode;
			api?.core?.actions.execute(({ tr }) => {
				api?.blockControls?.commands?.moveToLayout(from, to)({ tr });
				const insertColumnStep = getInsertLayoutStep(tr);
				mappedTo = (insertColumnStep as ReplaceStep)?.from;

				return tr;
			});

			api?.core?.actions.execute(({ tr }) => {
				if (mappedTo !== undefined) {
					updateSelection(tr, mappedTo);
				}
				return tr;
			});
		}
	}, [api, getPos, activeNode]);

	useEffect(() => {
		if (ref.current) {
			return dropTargetForElements({
				element: ref.current,
				onDragEnter: () => {
					setIsDraggedOver(true);
					readNextNodeAnchor();
				},
				onDragLeave: () => {
					setIsDraggedOver(false);
				},
				onDrop,
			});
		}
	}, [onDrop, readNextNodeAnchor]);

	if (activeNode?.nodeType === 'layoutSection') {
		return null;
	}
	return (
		<div
			ref={ref}
			// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
			css={[dropTargetLayoutStyle, dropTargetStackLayoutHintStyle]}
			data-testid="block-ctrl-drop-indicator"
		>
			{isDraggedOver ? (
				<DropIndicator edge="right" gap={`-${DROP_TARGET_LAYOUT_DROP_ZONE_WIDTH}px`} />
			) : (
				(isActiveAnchor ||
					expValEquals('platform_editor_native_anchor_with_dnd', 'isEnabled', true) ||
					isExperimentEnabled('platform_editor_block_control_migration')) && (
					<div data-testid="block-ctrl-drop-hint" css={dropTargetLayoutHintStyle}></div>
				)
			)}
		</div>
	);
};
